import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SearchableSelect } from './ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ArrowLeftRight, CheckCircle, AlertTriangle, Calendar, User, Bike, FileText } from 'lucide-react';
import { toast } from 'sonner';
import useDeviceDetection from '../hooks/useDeviceDetection';
import { formatErrorMessage } from '../utils/formatError';

const ReplacementMotorcycle = () => {
  const [motorcycles, setMotorcycles] = useState([]);
  const [riders, setRiders] = useState([]);
  const [pendingReplacements, setPendingReplacements] = useState([]);
  const [replacementHistory, setReplacementHistory] = useState([]);
  const { isMobile } = useDeviceDetection();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('provide');
  
  // Provide Replacement Form
  const [replacementForm, setReplacementForm] = useState({
    reason: '',
    original_bike_id: '',
    replacement_bike_id: '',
    replacement_start_date: new Date().toISOString().split('T')[0],
    expected_return_date: '',
    notes: '',
    replacement_description: '', // Manual description of reason
    location: 'Workshop'
  });
  
  // Return Replacement Form
  const [returnForm, setReturnForm] = useState({
    original_bike_id: '',
    replacement_bike_id: '',
    new_rider_id: '',
    return_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bikesRes, ridersRes, replacementsRes, historyRes] = await Promise.all([
        axios.get(`${API}/motorcycles`, { headers: getAuthHeader() }),
        axios.get(`${API}/riders`, { headers: getAuthHeader() }),
        axios.get(`${API}/motorcycles/pending-replacements`, { headers: getAuthHeader() }),
        axios.get(`${API}/motorcycles/replacement-history`, { headers: getAuthHeader() })
      ]);
      
      setMotorcycles(bikesRes.data);
      setRiders(ridersRes.data);
      setPendingReplacements(replacementsRes.data);
      setReplacementHistory(
        (historyRes.data || []).sort((a, b) => {
          const dateA = a.replacement_start_date || a.created_at || '';
          const dateB = b.replacement_start_date || b.created_at || '';
          return dateB.localeCompare(dateA);
        })
      );
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Get all assigned motorcycles (bikes with a rider assigned)
  const assignedBikes = motorcycles.filter(bike => 
    bike.assigned_rider_id || bike.rider_id || bike.assigned_rider_name
  );
  
  // Get accident bikes for selection
  const accidentBikes = motorcycles.filter(bike => bike.status === 'accident');
  
  // Get bikes available for call back (in-use status)
  const callBackBikes = motorcycles.filter(bike => 
    bike.status === 'in-use' && 
    (bike.assigned_rider_id || bike.rider_id)
  );
  
  // Get bikes eligible based on reason - now showing all assigned bikes for any reason
  const eligibleOriginalBikes = assignedBikes;
  
  // Get replacement bikes (available or same lessee)
  const getReplacementBikes = () => {
    const selectedOriginal = motorcycles.find(b => b.id === replacementForm.original_bike_id);
    if (!selectedOriginal) return [];
    
    const originalLessee = selectedOriginal.lessee_id;
    
    return motorcycles.filter(bike => 
      bike.id !== replacementForm.original_bike_id &&
      bike.status === 'available' &&
      (!bike.lessee_id || bike.lessee_id === originalLessee) &&
      !bike.is_replacement
    );
  };

  const handleProvideReplacement = async (e) => {
    e.preventDefault();
    
    if (!replacementForm.reason || !replacementForm.original_bike_id || !replacementForm.replacement_bike_id) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!replacementForm.replacement_description) {
      toast.error('Please provide a description of the replacement reason');
      return;
    }
    
    try {
      // Include description in the notes field for API compatibility
      const formDataWithDescription = {
        ...replacementForm,
        notes: `${replacementForm.replacement_description}${replacementForm.notes ? '\n\nAdditional notes: ' + replacementForm.notes : ''}`
      };
      
      const response = await axios.post(
        `${API}/motorcycles/provide-replacement`,
        formDataWithDescription,
        { headers: getAuthHeader() }
      );
      
      toast.success(response.data.message);
      
      // Reset form
      setReplacementForm({
        reason: '',
        original_bike_id: '',
        replacement_bike_id: '',
        replacement_start_date: new Date().toISOString().split('T')[0],
        expected_return_date: '',
        notes: '',
        replacement_description: '',
        location: 'Workshop'
      });
      
      // Refresh data
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to provide replacement'));
      console.error(error);
    }
  };

  const handleReturnReplacement = async (replacement) => {
    try {
      const data = {
        original_bike_id: replacement.original_bike.id,
        replacement_bike_id: replacement.replacement_bike.id,
        new_rider_id: returnForm.new_rider_id || null,
        return_date: returnForm.return_date
      };
      
      const response = await axios.post(
        `${API}/motorcycles/return-replacement`,
        data,
        { headers: getAuthHeader() }
      );
      
      toast.success(response.data.message);
      
      // Reset form
      setReturnForm({
        original_bike_id: '',
        replacement_bike_id: '',
        new_rider_id: '',
        return_date: new Date().toISOString().split('T')[0]
      });
      
      // Refresh data
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to return replacement'));
      console.error(error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'available': { color: 'bg-green-100 text-green-800', label: 'Available' },
      'in-use': { color: 'bg-blue-100 text-blue-800', label: 'In Use' },
      'maintenance': { color: 'bg-yellow-100 text-yellow-800', label: 'Maintenance' },
      'accident': { color: 'bg-red-100 text-red-800', label: 'Accident' }
    };
    
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-96">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className={`grid w-full ${isMobile ? 'grid-cols-3 gap-1' : 'grid-cols-3'}`}>
          <TabsTrigger value="provide" className={isMobile ? 'text-xs px-1' : ''}>
            {isMobile ? 'Provide' : 'Provide Replacement'}
          </TabsTrigger>
          <TabsTrigger value="return" className={isMobile ? 'text-xs px-1' : ''}>
            {isMobile ? 'Active' : 'Active Replacements'}
          </TabsTrigger>
          <TabsTrigger value="history" className={isMobile ? 'text-xs px-1' : ''}>
            History
          </TabsTrigger>
        </TabsList>

        {/* Provide Replacement Tab */}
        <TabsContent value="provide">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowLeftRight className="w-5 h-5" />
                Provide Replacement Motorcycle
              </CardTitle>
              <CardDescription>
                Assign a replacement motorcycle for accident repairs or call back maintenance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProvideReplacement} className="space-y-6">
                {/* Step 1: Select Reason */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Replacement Reason *</Label>
                  <Select
                    value={replacementForm.reason}
                    onValueChange={(value) => setReplacementForm({
                      ...replacementForm,
                      reason: value,
                      original_bike_id: '', // Reset selections when reason changes
                      replacement_bike_id: ''
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select reason..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="accident">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-500" />
                          <span>Accident - Bike needs repair</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="call_back_maintenance">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-500" />
                          <span>Call Back - Voluntary maintenance</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-600">
                    All assigned motorcycles will be shown for selection
                  </p>
                </div>

                {replacementForm.reason && (
                  <>
                    {/* Step 2: Select Original Bike */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Motorcycle to Replace *
                      </Label>
                      <SearchableSelect
                        options={eligibleOriginalBikes.map(bike => ({
                          value: bike.id,
                          label: bike.plate_number,
                          searchText: `${bike.plate_number} ${bike.model_name} ${bike.rider_name || ''} ${bike.lessee_name || ''} ${bike.assigned_rider_name || ''}`,
                          metadata: {
                            'Model': bike.model_name,
                            'Status': bike.status || 'N/A',
                            'Rider': bike.rider_name || bike.assigned_rider_name || 'N/A',
                            'Lessee': bike.lessee_name || 'N/A'
                          }
                        }))}
                        value={replacementForm.original_bike_id}
                        onValueChange={(value) => setReplacementForm({
                          ...replacementForm,
                          original_bike_id: value,
                          replacement_bike_id: '', // Reset replacement bike selection
                          replacement_description: '' // Reset description
                        })}
                        placeholder="Search assigned motorcycle..."
                        emptyMessage="No assigned motorcycles found."
                        showMetadata={true}
                      />
                      {eligibleOriginalBikes.length === 0 && (
                        <p className="text-xs text-red-600">
                          No assigned motorcycles found
                        </p>
                      )}
                    </div>

                    {/* Step 2b: Description of Reason (after bike selected) */}
                    {replacementForm.original_bike_id && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Description of Replacement Reason *</Label>
                        <textarea
                          className="w-full min-h-[100px] p-3 border rounded-md border-slate-300 focus:border-green-500 focus:ring-green-500 text-sm"
                          placeholder="Please describe the reason for replacement (e.g., engine issue, electrical fault, accident damage, routine maintenance, etc.)"
                          value={replacementForm.replacement_description}
                          onChange={(e) => setReplacementForm({
                            ...replacementForm,
                            replacement_description: e.target.value
                          })}
                          required
                        />
                        <p className="text-xs text-gray-600">
                          Provide details about why this motorcycle needs to be replaced
                        </p>
                      </div>
                    )}

                    {/* Step 3: Select Replacement Bike */}
                    {replacementForm.original_bike_id && replacementForm.replacement_description && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Replacement Bike *</Label>
                        <SearchableSelect
                          options={getReplacementBikes().map(bike => ({
                            value: bike.id,
                            label: bike.plate_number,
                            searchText: `${bike.model_name} ${bike.lessee_name || 'Unassigned'}`,
                            metadata: {
                              'Model': bike.model_name,
                              'Status': bike.status,
                              'Lessee': bike.lessee_name || 'Unassigned'
                            }
                          }))}
                          value={replacementForm.replacement_bike_id}
                          onValueChange={(value) => setReplacementForm({
                            ...replacementForm,
                            replacement_bike_id: value
                          })}
                          placeholder="Search available bike..."
                          emptyMessage="No available bikes with matching lessee found."
                          showMetadata={true}
                        />
                        <p className="text-xs text-gray-600">
                          Showing available bikes with same lessee or unassigned bikes
                        </p>
                      </div>
                    )}

                    {/* Step 4: Dates */}
                    {replacementForm.replacement_bike_id && (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Replacement Start Date *</Label>
                            <Input
                              type="date"
                              value={replacementForm.replacement_start_date}
                              onChange={(e) => setReplacementForm({
                                ...replacementForm,
                                replacement_start_date: e.target.value
                              })}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Expected Return Date</Label>
                            <Input
                              type="date"
                              value={replacementForm.expected_return_date}
                              onChange={(e) => setReplacementForm({
                                ...replacementForm,
                                expected_return_date: e.target.value
                              })}
                              min={replacementForm.replacement_start_date}
                            />
                          </div>
                        </div>

                        {/* Step 5: Additional Info */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Location</Label>
                          <Input
                            value={replacementForm.location}
                            onChange={(e) => setReplacementForm({
                              ...replacementForm,
                              location: e.target.value
                            })}
                            placeholder="Workshop location"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Notes</Label>
                          <textarea
                            className="w-full border rounded-md p-2 min-h-[80px]"
                            value={replacementForm.notes}
                            onChange={(e) => setReplacementForm({
                              ...replacementForm,
                              notes: e.target.value
                            })}
                            placeholder="Additional notes about the replacement..."
                          />
                        </div>

                        <Button type="submit" className="w-full">
                          <ArrowLeftRight className="w-4 h-4 mr-2" />
                          Provide Replacement Motorcycle
                        </Button>
                      </>
                    )}
                  </>
                )}
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Active Replacements Tab */}
        <TabsContent value="return">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Active Replacements
              </CardTitle>
              <CardDescription>
                Manage active replacements and return original bikes to riders
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingReplacements.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ArrowLeftRight className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No active replacements</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingReplacements.map((replacement, index) => (
                    <Card key={index} className="border-2">
                      <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {/* Original Bike */}
                          <div className="space-y-3">
                            <h3 className="font-semibold text-sm text-gray-600">Original Bike</h3>
                            <div className="space-y-2">
                              <div>
                                <p className="text-xs text-gray-500">Plate Number</p>
                                <p className="font-semibold">{replacement.original_bike.plate_number}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Status</p>
                                {getStatusBadge(replacement.original_bike.status)}
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Model</p>
                                <p className="text-sm">{replacement.original_bike.model_name}</p>
                              </div>
                              {replacement.original_bike.lessee_name && (
                                <div>
                                  <p className="text-xs text-gray-500">Lessee</p>
                                  <p className="text-sm">{replacement.original_bike.lessee_name}</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Replacement Info */}
                          <div className="space-y-3 border-l pl-6">
                            <h3 className="font-semibold text-sm text-gray-600">Replacement Info</h3>
                            <div className="space-y-2">
                              <div>
                                <p className="text-xs text-gray-500">Reason</p>
                                <Badge className={replacement.replacement_bike.replacement_reason === 'accident' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}>
                                  {replacement.replacement_bike.replacement_reason === 'accident' ? 'Accident' : 'Call Back Maintenance'}
                                </Badge>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Replacement Bike</p>
                                <p className="font-semibold">{replacement.replacement_bike.plate_number}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Rider</p>
                                <p className="text-sm">{replacement.replacement_bike.original_rider_name || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Start Date</p>
                                <p className="text-sm">{replacement.replacement_bike.replacement_start_date}</p>
                              </div>
                              {replacement.replacement_bike.expected_return_date && (
                                <div>
                                  <p className="text-xs text-gray-500">Expected Return</p>
                                  <p className="text-sm">{replacement.replacement_bike.expected_return_date}</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Return Action */}
                          <div className="space-y-3 border-l pl-6">
                            <h3 className="font-semibold text-sm text-gray-600">Return to Rider</h3>
                            {replacement.can_return ? (
                              <div className="space-y-3">
                                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                                  <div className="flex items-center gap-2 text-green-800 mb-2">
                                    <CheckCircle className="w-4 h-4" />
                                    <p className="text-sm font-semibold">Ready to Return</p>
                                  </div>
                                  <p className="text-xs text-green-700">
                                    Original bike is repaired and available
                                  </p>
                                </div>

                                <div className="space-y-2">
                                  <Label className="text-xs">Assign to New Rider (Optional)</Label>
                                  <SearchableSelect
                                    options={riders.filter(r => r.status === 'active').map(rider => ({
                                      value: rider.id,
                                      label: rider.name,
                                      searchText: `${rider.emirates_id || ''} ${rider.phone || ''}`,
                                      metadata: {
                                        'Emirates ID': rider.emirates_id,
                                        'Phone': rider.phone
                                      }
                                    }))}
                                    value={returnForm.new_rider_id}
                                    onValueChange={(value) => setReturnForm({
                                      ...returnForm,
                                      new_rider_id: value
                                    })}
                                    placeholder="Keep original rider..."
                                    emptyMessage="No active riders found."
                                    showMetadata={false}
                                  />
                                  <p className="text-xs text-gray-500">
                                    Leave blank to return to original rider
                                  </p>
                                </div>

                                <div className="space-y-2">
                                  <Label className="text-xs">Return Date</Label>
                                  <Input
                                    type="date"
                                    value={returnForm.return_date}
                                    onChange={(e) => setReturnForm({
                                      ...returnForm,
                                      return_date: e.target.value
                                    })}
                                  />
                                </div>

                                <Button 
                                  onClick={() => handleReturnReplacement(replacement)}
                                  className="w-full"
                                  variant="default"
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Return to Rider
                                </Button>
                              </div>
                            ) : (
                              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                                <div className="flex items-center gap-2 text-yellow-800 mb-2">
                                  <AlertTriangle className="w-4 h-4" />
                                  <p className="text-sm font-semibold">Maintenance In Progress</p>
                                </div>
                                <p className="text-xs text-yellow-700">
                                  Original bike is still under {replacement.replacement_bike.replacement_reason === 'accident' ? 'accident repair' : 'maintenance'}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Replacement History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Replacement History & Audit Trail
              </CardTitle>
              <CardDescription>
                Complete history of all motorcycle replacements
              </CardDescription>
            </CardHeader>
            <CardContent>
              {replacementHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No replacement history found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {replacementHistory.map((entry, index) => (
                    <Card 
                      key={entry.id} 
                      className={`border-l-4 ${
                        entry.event_type === 'replacement_provided' 
                          ? 'border-l-blue-500 bg-blue-50' 
                          : 'border-l-green-500 bg-green-50'
                      }`}
                    >
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              entry.event_type === 'replacement_provided' 
                                ? 'bg-blue-100' 
                                : 'bg-green-100'
                            }`}>
                              {entry.event_type === 'replacement_provided' ? (
                                <ArrowLeftRight className="w-5 h-5 text-blue-600" />
                              ) : (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              )}
                            </div>
                            <div>
                              <h3 className="font-semibold text-sm">
                                {entry.event_type === 'replacement_provided' 
                                  ? 'Replacement Provided' 
                                  : 'Replacement Returned'}
                              </h3>
                              <p className="text-xs text-gray-600">
                                {new Date(entry.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <Badge className={
                            entry.reason === 'accident' 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-blue-100 text-blue-800'
                          }>
                            {entry.reason === 'accident' ? 'Accident' : 'Call Back Maintenance'}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
                          <div>
                            <p className="text-gray-500 font-medium mb-1">Original Bike</p>
                            <p className="font-semibold text-gray-900">{entry.original_bike_plate}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 font-medium mb-1">Replacement Bike</p>
                            <p className="font-semibold text-gray-900">{entry.replacement_bike_plate}</p>
                          </div>
                          {entry.rider_name && (
                            <div>
                              <p className="text-gray-500 font-medium mb-1">Rider</p>
                              <p className="text-gray-900">{entry.rider_name}</p>
                              {entry.new_rider_name && entry.original_rider_became_inactive && (
                                <p className="text-xs text-orange-600 mt-1">
                                  ⚠️ Original rider became inactive
                                </p>
                              )}
                            </div>
                          )}
                          {entry.lessee_name && (
                            <div>
                              <p className="text-gray-500 font-medium mb-1">Lessee</p>
                              <p className="text-gray-900">{entry.lessee_name}</p>
                            </div>
                          )}
                        </div>

                        {entry.event_type === 'replacement_provided' && (
                          <div className="grid grid-cols-2 gap-4 mt-3 text-xs">
                            <div>
                              <p className="text-gray-500 font-medium mb-1">Start Date</p>
                              <p className="text-gray-900">{entry.replacement_start_date}</p>
                            </div>
                            {entry.expected_return_date && (
                              <div>
                                <p className="text-gray-500 font-medium mb-1">Expected Return</p>
                                <p className="text-gray-900">{entry.expected_return_date}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {entry.event_type === 'replacement_returned' && entry.actual_return_date && (
                          <div className="mt-3 text-xs">
                            <p className="text-gray-500 font-medium mb-1">Returned On</p>
                            <p className="text-gray-900">{entry.actual_return_date}</p>
                          </div>
                        )}

                        {entry.notes && (
                          <div className="mt-3 pt-3 border-t text-xs">
                            <p className="text-gray-500 font-medium mb-1">Notes</p>
                            <p className="text-gray-700 whitespace-pre-line">{entry.notes}</p>
                          </div>
                        )}

                        <div className="mt-3 pt-3 border-t flex justify-between items-center text-xs text-gray-500">
                          <span>Performed by: {entry.performed_by}</span>
                          {entry.maintenance_id && (
                            <span>Maintenance ID: {entry.maintenance_id.substring(0, 8)}...</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReplacementMotorcycle;
